import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';

import { Trip } from '@fcom/common/interfaces';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { ButtonMode, ButtonTheme } from '@fcom/ui-components/components/buttons';
import { IconPosition } from '@fcom/ui-components/components/icons';
import { LoaderTheme } from '@fcom/ui-components/components/loader';
import { MediaQueryService, ScrollService } from '@fcom/common/services';
import { RedirectActions } from '@fcom/common-booking';
import { RootPaths } from '@fcom/core/constants';
import { AppState } from '@fcom/core/interfaces';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { profile } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx/operators/finShare';
import { CorporateUserRole, Profile } from '@fcom/core-api/login';
import { isCorporateAdminOrTravelBooker } from '@fcom/common/login';
import { toISO8601 } from '@fcom/core/utils';

import { TripRow } from '../../trips-view/trips-view.component';

enum WidgetTripType {
  COMPANY_TRIPS,
  MY_TRIPS,
}

@Component({
  selector: 'fin-next-trips-widget',
  templateUrl: './next-trips-widget.component.html',
  styleUrl: './next-trips-widget.component.scss',
})
export class NextTripsWidgetComponent implements OnInit {
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonMode = ButtonMode;
  protected readonly IconPosition = IconPosition;
  protected readonly LoaderTheme = LoaderTheme;
  protected readonly WidgetTripType = WidgetTripType;

  readonly NUMBER_OF_TRIPS = 3;

  trips$: Observable<Trip[]>;
  profile$: Observable<Profile>;
  isMobile$: Observable<boolean>;
  loading = false;
  enableTabs$: Observable<boolean>;

  widgetTripType$: BehaviorSubject<WidgetTripType> = new BehaviorSubject(WidgetTripType.COMPANY_TRIPS);

  constructor(
    private store$: Store<AppState>,
    private corporateService: CorporateService,
    private scrollService: ScrollService,
    private languageService: LanguageService,
    private mediaQueryService: MediaQueryService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.trips$ = this.widgetTripType$.pipe(
      distinctUntilChanged(),
      tap(() => (this.loading = true)),
      switchMap((widgetTripType) =>
        this.corporateService.getTrips({
          from: 0,
          size: this.NUMBER_OF_TRIPS,
          filter: {
            myTrips: widgetTripType === WidgetTripType.MY_TRIPS,
            dateRange: {
              fromDate: toISO8601(new Date()),
              toDate: undefined,
            },
          },
        })
      ),
      map((tripsResponse) => tripsResponse.trips),
      tap(() => (this.loading = false))
    );

    this.profile$ = this.store$.pipe(
      profile(),
      tap((userProfile) => {
        if (userProfile?.corporate?.userRole === CorporateUserRole.TRAVELLER) {
          this.widgetTripType$.next(WidgetTripType.MY_TRIPS);
        }
      }),
      finShare()
    );
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('tablet_down');
    this.enableTabs$ = this.profile$.pipe(map((userProfile) => isCorporateAdminOrTravelBooker(userProfile)));
  }

  navigateToTrips(): void {
    this.router.navigate(['../trips'], { relativeTo: this.activatedRoute });
  }

  manageBooking(trip: TripRow): void {
    this.store$.dispatch(
      RedirectActions.setPNR({ pnr: { lastName: trip.passengers[0].lastName, recLoc: trip.bookingReference } })
    );
    this.router.navigate([this.languageService.langValue, RootPaths.MANAGE_BOOKING_ROOT]);
  }

  scrollToBookingWidget(): void {
    const bookingWidget: HTMLElement = this.document.querySelector('fin-booking-widget');
    this.scrollService.smoothScroll(bookingWidget, { offsetY: 140 });
  }

  tabChanged(index: number): void {
    this.widgetTripType$.next(index);
  }
}
